import { Autocomplete } from '@react-google-maps/api';
import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { geocodingServices, mapServices } from 'src/services';
import { hideLoadingModalReducer, setAlertResponse, showLoadingModalReducer } from 'src/store';
import { typeMapDoc, typeMapFamilia, typeMapToast } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode } from 'src/custom-hooks';

type propsType = {
    mapDoc: typeMapDoc;
    setToast: Dispatch<SetStateAction<typeMapToast | null>>;
};

const initialForm = { familia: '', direccion: '', diasHorarios: '', activo: true };

export const HTHInteractiveMapFamilies: FC<propsType> = ({ mapDoc, setToast }) => {
    const [adding, setAdding] = useState(false);
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
    const [editId, setEditId] = useState<number | null>(null);
    const [familias, setFamilias] = useState<typeMapFamilia[]>(mapDoc.familias);
    const [form, setForm] = useState(initialForm);
    const [geocodingLoading, setGeocodingLoading] = useState(false);
    const dispatch = useDispatch();
    const familiaInputRef = useRef<HTMLInputElement>(null);
    const isDarkMode = useGetIsDarkMode();

    const resetForm = () => {
        setForm(initialForm);
        setEditId(null);
        setAdding(false);
    }

    const handleAddFamilia = async (e: React.FormEvent) => {
        e.preventDefault();
        setGeocodingLoading(true);
        const response = await geocodingServices.GetCoordinatesFromAddress(form.direccion);
        setGeocodingLoading(false);
        if (!response.success || !response.data?.coordinates?.lat || !response.data.coordinates?.lng) {
            return;
        }
        const lat = response.data.coordinates.lat;
        const lng = response.data.coordinates.lng;
        if (editId) {
            const familiaToUpdate = familias.find(f => f.id === editId);
            if (familiaToUpdate) {
                const updatedFamilia = { ...familiaToUpdate, ...form, lat, lng };
                dispatch(showLoadingModalReducer());
                const response = await mapServices.UpdateFamilia(updatedFamilia);
                dispatch(hideLoadingModalReducer());
                if (response.success) {
                    const nuevas = familias.map(f =>
                        f.id === editId ? updatedFamilia : f
                    );
                    setFamilias(nuevas);
                    setEditId(null);
                    setToast({ msg: 'Familia modificada exitosamente', type: 'success' });
                } else {
                    setToast({ msg: 'Error al modificar familia', type: 'danger' });
                    dispatch(setAlertResponse({ response }));
                }
            }
        } else {
            const nuevaFamilia: typeMapFamilia = {
                activo: form.activo,
                diasHorarios: form.diasHorarios,
                direccion: form.direccion,
                familia: form.familia,
                id: 0,
                lat,
                lng
            };
            dispatch(showLoadingModalReducer());
            const response = await mapServices.AddFamilia(nuevaFamilia);
            dispatch(hideLoadingModalReducer());
            if (response.success) {
                const nuevas = [...familias, nuevaFamilia];
                setFamilias(nuevas);
                setToast({ msg: 'Familia agregada exitosamente', type: 'success' });
            } else {
                setToast({ msg: 'Error al agregar familia', type: 'danger' });
                dispatch(setAlertResponse({ response }));
            }
        }
        setForm({ familia: '', direccion: '', diasHorarios: '', activo: true });
        setAdding(false);
    }

    const handleDeleteFamilia = async (id: number) => {
        dispatch(showLoadingModalReducer());
        const response = await mapServices.DeleteFamilia(id);
        dispatch(hideLoadingModalReducer());
        if (response.success) {
            const nuevas = familias.filter(f => f.id !== id);
            setFamilias(nuevas);
            setToast({ msg: 'Familia eliminada exitosamente', type: 'success' });
        } else {
            setToast({ msg: 'Error al eliminar familia', type: 'danger' });
            dispatch(setAlertResponse({ response }));
        }
    }

    const handleToggleActivo = async (id: number) => {
        const familiaToUpdate = familias.find(f => f.id === id);
        if (familiaToUpdate) {
            const updatedFamilia = { ...familiaToUpdate, activo: !familiaToUpdate.activo };
            dispatch(showLoadingModalReducer());
            const response = await mapServices.UpdateFamilia(updatedFamilia);
            dispatch(hideLoadingModalReducer());
            if (response.success) {
                const nuevas = familias.map(f => f.id === id ? updatedFamilia : f);
                setFamilias(nuevas);
                setToast({ msg: 'Estado de familia actualizado', type: 'success' });
            } else {
                setToast({ msg: 'Error al actualizar familia', type: 'danger' });
                dispatch(setAlertResponse({ response }));
            }
        }
    }

    useEffect(() => {
        if (adding && familiaInputRef.current) {
            familiaInputRef.current.focus();
        }
    }, [adding]);

    return (
        <div className={'mt-5'}>
            <h3 className={'text-center mb-3'}>
                Familias en el mapa
            </h3>
            <button className={'btn btn-success mb-4'}
                onClick={() => {
                    if (adding) {
                        resetForm();
                    } else {
                        setAdding(true);
                        setEditId(null);
                    }
                }}
            >
                {adding ? 'Cerrar formulario' : 'Agregar familia'}
            </button>
            {adding &&
                <form className={`card ${isDarkMode ? 'bg-lightdark border-light' : 'bg-light border'} border-1 rounded-6 shadow-sm p-3 mb-3`}
                    onSubmit={handleAddFamilia}
                >
                    <div className={'row align-items-end g-2'}>
                        <div className={'col-md-3'}>
                            <input type={'text'}
                                ref={familiaInputRef}
                                className={'form-control'}
                                placeholder={'Familia'}
                                value={form.familia}
                                onChange={e => setForm(f => ({ ...f, familia: e.target.value }))}
                                required
                            />
                        </div>
                        <div className={'col-md-3'}>
                            <Autocomplete
                                onLoad={ac => setAutocomplete(ac)}
                                onPlaceChanged={() => {
                                    if (autocomplete) {
                                        const place = autocomplete.getPlace();
                                        const direccion = place.formatted_address || place.name || '';
                                        setForm(f => ({ ...f, direccion }));
                                    }
                                }}
                            >
                                <input type={'text'}
                                    className={'form-control'}
                                    placeholder={'Dirección'}
                                    value={form.direccion}
                                    onChange={e => setForm(f => ({ ...f, direccion: e.target.value }))}
                                    required
                                />
                            </Autocomplete>
                        </div>
                        <div className={'col-md-3'}>
                            <input type={'text'}
                                className={'form-control'}
                                placeholder={'Días y horarios'}
                                value={form.diasHorarios}
                                onChange={e => setForm(f => ({ ...f, diasHorarios: e.target.value }))}
                                required
                            />
                        </div>
                        <div className={'col-md-3 d-flex justify-content-end gap-2'}>
                            <button type={'submit'}
                                className={'btn btn-general-blue'}
                                disabled={geocodingLoading}
                            >
                                {editId ? 'Modificar' : 'Agregar'}
                            </button>
                            <button className={'btn btn-general-red'}
                                type={'button'}
                                onClick={resetForm}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </form>
            }
            <div className={'table-responsive'}>
                <table className={`table table-bordered ${isDarkMode ? 'table-dark' : ''} table-sm align-middle`}>
                    <thead>
                        <tr>
                            <th>Familia</th>
                            <th>Dirección</th>
                            <th>Descripción y disponibilidad</th>
                            <th>Activo</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {familias.map(f =>
                            <tr key={f.id}>
                                <td>
                                    {f.familia}
                                </td>
                                <td>
                                    {f.direccion}
                                </td>
                                <td>
                                    {f.diasHorarios}
                                </td>
                                <td className={'text-center'}>
                                    <input type={'checkbox'}
                                        className={'form-check-input checkbox-blue checkbox-md me-2'}
                                        checked={f.activo}
                                        onChange={() => handleToggleActivo(f.id)} 
                                    />
                                </td>
                                <td className={'text-nowrap'}>
                                    <button className={'btn btn-general-blue btn-sm me-1'}
                                        onClick={() => {
                                            setForm({
                                                familia: f.familia,
                                                direccion: f.direccion,
                                                diasHorarios: f.diasHorarios,
                                                activo: f.activo
                                            });
                                            setEditId(f.id);
                                            setAdding(true);
                                        }}
                                    >
                                        Modificar
                                    </button>
                                    <button className={'btn btn-general-red btn-sm'}
                                        onClick={() => handleDeleteFamilia(f.id)}
                                    >
                                        Baja
                                    </button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
